import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import * as messageSelector from './message.selector';
import { combineLatestWith, map } from 'rxjs/operators';
import {
	badgeGlobalSecondary,
	BaseMessage,
	blueBgClass,
	MarketMessage,
	MessageTypes,
	pinkRoseBgClass,
	redBgClass
} from '@easy-lib/modules/messages';
import { Observable } from 'rxjs/internal/Observable';
import { of } from 'rxjs/internal/observable/of';
import { PortfolioService } from '../../modules/portfolio';
import { firstValueFrom } from 'rxjs';
import { OrderStore } from '../orders';
import { PortfolioStore } from '@easy-lib/state/portfolio/portfolio.store';
import { UnleashService } from '@easy-lib/modules/shared/services/unleash.service';
import { PositionStore } from '../position/position.store';

@Injectable({ providedIn: 'root' })
export class MessageStore {
	constructor(
		private store: Store,
		private portfolioService: PortfolioService,
		private portfolioStore: PortfolioStore,
		private orderStore: OrderStore,
		private unleashService: UnleashService,
		private positionStore: PositionStore
	) {}

	marketMessages$ = this.store.select(messageSelector.getMarketMessages);
	marketAndImmediateMessages$ = this.store.select(messageSelector.getMarketAndImmediateMessages);
	unreadMarketMessages$ = this.store.select(messageSelector.getUnreadMarketMessages);
	unreadMarketMessagesCount$ = this.store.select(messageSelector.getUnreadMarketMessagesCount);
	marketLastUpdateTime$ = this.store.select(messageSelector.getMessagesLastUpdateTime);
	mofidlastUpdateTime$ = this.store.select(messageSelector.getMofidMessagesLastUpdateTime);
	loading$ = this.store.select(messageSelector.getMessagesLoading);

	mofidMessages$ = this.store.select(messageSelector.getMofidMessages);
	unreadMofidMessages$ = this.store.select(messageSelector.getUnreadMofidMessages);
	unreadMofidMessagesCount$ = this.store.select(messageSelector.getUnreadMofidMessagesCount);
	mofidMessagePageState$ = this.store.select(messageSelector.getMofidMessagePageState);
	mofidMessageHasLoadMore$ = this.store.select(messageSelector.getMofidMessageLoadMore);
	mofidMessageImages$ = this.store.select(messageSelector.getMofidMessageImages);

	immediateMessages$ = this.store.select(messageSelector.getImmediateMessages);
	unreadImmediateMessages$ = this.store.select(messageSelector.getUnreadImmediateMessages);
	unreadImmediateMessagesCount$ = this.store.select(messageSelector.getUnreadImmediateMessagesCount);

	orders$ = this.orderStore.getOrders$();
	portfolios$ = this.portfolioStore.portfolioItems$;

	userMessages$ = this.marketAndImmediateMessages$.pipe(
		combineLatestWith(this.orders$, this.portfolios$, this.positionStore.PositionIsins$),
		map(([messages, orders, portfolios, positionIsins]) => {
			const orderIsins = orders?.map(t => t.symbolIsin) || [];
			const portfoIsins = portfolios?.map(t => t.symbolIsin) || [];
			const isins = orderIsins.concat(portfoIsins, positionIsins);
			return messages.filter((message: MarketMessage) => isins.some(t => message.isins?.includes(t)));
		})
	);
	unreadUserMessages$ = this.userMessages$.pipe(
		map((messages: MarketMessage[]) => messages.filter(t => !t.isRead || !t.isSeen))
	);
	unreadUserImmediateMessages$ = this.unreadUserMessages$.pipe(
		map(messages => messages.filter(t => t.type === MessageTypes.ImmediateMessage))
	);
	unreadUserMessagesCount$ = this.unreadUserMessages$.pipe(map(messages => messages.length));
	unreadImmediateUserMessagesCount$ = this.unreadUserImmediateMessages$.pipe(map(messages => messages.length));
	unreadMessages$ = this.store.select(messageSelector.getUnreadMessages);
	unreadMessageState$ = this.unreadImmediateMessagesCount$.pipe(
		combineLatestWith(
			this.unreadImmediateUserMessagesCount$,
			this.unreadUserMessagesCount$,
			this.unreadMarketMessagesCount$,
			this.unreadMofidMessagesCount$
		),
		map(
			([
				unreadImmediateCount,
				unreadImmediateUserMessagesCount,
				unreadUserMessagesCount,
				unreadMarketMessagesCount,
				unreadMofidMessageCount
			]) => {
				if (unreadImmediateUserMessagesCount)
					return this.prepareMessageState(pinkRoseBgClass, unreadUserMessagesCount, MessageTypes.UserMessage);

				if (unreadImmediateCount)
					return this.prepareMessageState(redBgClass, unreadImmediateCount, MessageTypes.ImmediateMessage);

				if (unreadUserMessagesCount)
					return this.prepareMessageState(pinkRoseBgClass, unreadUserMessagesCount, MessageTypes.UserMessage);

				if (unreadMofidMessageCount)
					return this.prepareMessageState(
						badgeGlobalSecondary,
						unreadMofidMessageCount,
						MessageTypes.MofidMessage
					);

				return this.prepareMessageState(blueBgClass, unreadMarketMessagesCount, MessageTypes.MarketMessage);
			}
		)
	);
	getImmediateMessagesBySymbolIsin$(symbolIsin: string) {
		return this.store.select(messageSelector.getImmediateMessagesBySymbol(symbolIsin));
	}
	getImmediateMessagesCountBySymbolIsin$(symbolIsin: string) {
		return this.getImmediateMessagesBySymbolIsin$(symbolIsin).pipe(map(t => !!t.length));
	}
	getUnreadMarketMessagesBySymbolIsin$(symbolIsin: string) {
		return this.store.select(messageSelector.getUnreadMarketMessagesBySymbol(symbolIsin));
	}
	getUnreadMarketMessagesCountBySymbolIsin$(symbolIsin: string) {
		return this.getUnreadMarketMessagesBySymbolIsin$(symbolIsin).pipe(map(t => t.length));
	}
	getUnreadImmediateMessagesCountBySymbol$(symbolIsin: string) {
		return this.store
			.select(messageSelector.getUnreadImmediateMessagesBySymbol(symbolIsin))
			.pipe(map(t => t.length));
	}
	getUnreadMofidMessagesCountBySymbolIsin$(symbolIsin: string) {
		return this.store.select(messageSelector.getUnreadMofidMessagesBySymbol(symbolIsin)).pipe(map(t => t.length));
	}
	getUnreadMessagesCountBySymbolIsin$(symbolIsin: string) {
		return this.store.select(messageSelector.getUnreadMessagesBySymbol(symbolIsin)).pipe(map(t => t.length));
	}
	getUnreadMessagesStateBySymbolIsin$(symbolIsin: string) {
		return this.store.select(messageSelector.getUnreadMessagesBySymbol(symbolIsin)).pipe(
			map(messages => messages.groupBy('type')),
			map((res: Record<number, BaseMessage[]>) =>
				Object.entries(res).map(([type, message]) => ({
					type,
					count: message.length,
					state: this.getMessagesState[type]
				}))
			),
			map(res => res.filter(t => t.count > 0).first())
		);
	}
	async getMessageIndexById(id: string) {
		return await firstValueFrom(this.store.select(messageSelector.getMessageIndexById(id)));
	}
	async getMessagesBySymbol(symbolIsin: string) {
		return await firstValueFrom(this.store.select(messageSelector.getMessagesBySymbol(symbolIsin)));
	}
	async getUnreadMessagesBySymbol(symbolIsin: string) {
		return await firstValueFrom(this.store.select(messageSelector.getUnreadMarketMessagesBySymbol(symbolIsin)));
	}
	async countPortfolioMarketMessagesForSymbol(symbol: string) {
		const messages = await this.getUnreadMessagesBySymbol(symbol);
		return messages.length;
	}
	prepareMessageState(state: string, count?: number, messageType?: MessageTypes) {
		return { state, count, messageType };
	}
	getUnreadMessages$: { [key in MessageTypes]: Observable<BaseMessage[]> } = {
		[MessageTypes.MarketMessage]: this.unreadMarketMessages$,
		[MessageTypes.ImmediateMessage]: this.unreadImmediateMessages$,
		[MessageTypes.UserMessage]: this.unreadUserMessages$,
		[MessageTypes.MofidMessage]: this.unreadMofidMessages$,
		[MessageTypes.LoginMessage]: of([]),
		[MessageTypes.CodalMessage]: of([])
	};
	getMessagesState: { [key in MessageTypes]: string } = {
		[MessageTypes.MarketMessage]: blueBgClass,
		[MessageTypes.ImmediateMessage]: redBgClass,
		[MessageTypes.UserMessage]: pinkRoseBgClass,
		[MessageTypes.MofidMessage]: badgeGlobalSecondary,
		[MessageTypes.LoginMessage]: '',
		[MessageTypes.CodalMessage]: ''
	};
}
